import type { AppRouter } from '@fieldops/api/src/trpc/routers/index.js'
import { onlineManager, QueryClient } from '@tanstack/react-query'
import { createTRPCClient, httpBatchLink, TRPCClientError } from '@trpc/client'
import type { inferRouterOutputs } from '@trpc/server'
import { createTRPCContext } from '@trpc/tanstack-react-query'
import superjson from 'superjson'

/**
 * The API client.
 *
 * Types come straight from the server router, so a renamed field or a changed
 * input is a compile error here rather than a runtime surprise in front of a
 * customer.
 */

export const { TRPCProvider, useTRPC } = createTRPCContext<AppRouter>()

/**
 * What a procedure returns, for components that take a row as a prop and so
 * cannot infer it from the `useQuery` call site.
 */
export type RouterOutputs = inferRouterOutputs<AppRouter>

/** Native builds talk to an absolute origin; the web build is same-origin. */
const apiUrl = import.meta.env['VITE_API_URL'] ?? ''

/**
 * Capacitor cannot rely on a first-party cookie, so the token is kept and
 * sent as a bearer header there. On the web the cookie does the work and this
 * is never populated.
 */
let bearerToken: string | null = localStorage.getItem('fieldops_token')

export function setBearerToken(token: string | null): void {
  bearerToken = token
  if (token) localStorage.setItem('fieldops_token', token)
  else localStorage.removeItem('fieldops_token')
}

/** For the file URLs, which cannot carry a header. Null on the web build. */
export function currentBearerToken(): string | null {
  return bearerToken
}

/** Where the API is, for the URLs a browser fetches itself rather than us. */
export const apiOrigin = apiUrl

/**
 * A request that never reached the API: no network, a name that would not
 * resolve, or the proxy answering for a server that is restarting. tRPC keeps
 * it as the `cause` of the error it raises, which is how `isUnreachable`
 * tells it apart from an error the server meant.
 */
class UnreachableError extends Error {}

/**
 * What Fly's proxy answers while no machine is up. The API sends these codes
 * itself too (`BAD_GATEWAY` when a provider fails), but as JSON, so only a
 * non-JSON one counts as the server being gone.
 */
const GATEWAY_STATUSES = new Set([502, 503, 504])

const PROBE_EVERY_MS = 5_000
let probe: ReturnType<typeof setInterval> | undefined

/**
 * Tells React Query the API is gone. Queries then pause where they stand, so
 * whatever is on screen stays there, instead of failing into an error that
 * replaces it. `/health` is asked every few seconds until it answers.
 */
function markUnreachable(): void {
  onlineManager.setOnline(false)
  if (probe !== undefined) return
  probe = setInterval(() => {
    void fetch(`${apiUrl}/health`, { cache: 'no-store' }).then(
      (response) => {
        if (response.ok) markReachable()
      },
      () => {
        // Still gone. The next probe asks again.
      },
    )
  }, PROBE_EVERY_MS)
}

/**
 * The API answered. Paused queries resume and stale ones refetch, by
 * themselves — nobody has to press Try again.
 */
export function markReachable(): void {
  clearInterval(probe)
  probe = undefined
  onlineManager.setOnline(true)
}

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: `${apiUrl}/trpc`,
      transformer: superjson,
      fetch: async (url, options) => {
        let response: Response
        try {
          response = await fetch(url, {
            ...options,
            // Sends the session cookie on the web build.
            credentials: 'include',
          })
        } catch (error) {
          // A network failure is a TypeError in every browser. A cancelled
          // query aborts its own request, which is not the network.
          if (!(error instanceof TypeError)) throw error
          markUnreachable()
          throw new UnreachableError(error.message, { cause: error })
        }
        if (
          GATEWAY_STATUSES.has(response.status) &&
          !response.headers.get('content-type')?.includes('json')
        ) {
          markUnreachable()
          throw new UnreachableError(`The server answered ${response.status}.`)
        }
        markReachable()
        return response
      },
      headers: () => (bearerToken ? { authorization: `Bearer ${bearerToken}` } : {}),
    }),
  ],
})

export function isUnauthorised(error: unknown): boolean {
  return error instanceof TRPCClientError && error.data?.code === 'UNAUTHORIZED'
}

export function isUnreachable(error: unknown): boolean {
  return error instanceof TRPCClientError && error.cause instanceof UnreachableError
}

export function fieldErrors(error: unknown): Record<string, string[]> {
  if (!(error instanceof TRPCClientError)) return {}
  return (error.data?.fieldErrors as Record<string, string[]>) ?? {}
}

export function errorMessage(error: unknown): string {
  // Never the browser's own words: "Failed to fetch" means nothing to the
  // office, and Safari's "Load failed" even less.
  if (isUnreachable(error)) return "Can't connect right now. Check your internet connection and try again."
  if (error instanceof TRPCClientError) return error.message
  if (error instanceof Error) return error.message
  return 'Something went wrong.'
}

export function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Realtime invalidates precisely when something changes, so polling
        // and refetch-on-focus are redundant noise. Data is fresh because the
        // server said so, not because we asked again.
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // Re-requesting something you are not allowed to see will not start
          // working on the third attempt.
          if (isUnauthorised(error)) return false
          if (error instanceof TRPCClientError && error.data?.code === 'FORBIDDEN') return false
          // An unreachable API takes React Query offline, and a retry waits,
          // paused, until it answers again. These attempts are only spent
          // once it is back, so a long outage does not use them up.
          if (isUnreachable(error)) return failureCount < 5
          return failureCount < 2
        },
      },
      // A change made while offline fails there and then, with a message,
      // rather than waiting paused and going out unseen when the connection
      // returns: a text that sends itself an hour after you gave up on it is
      // worse than an error.
      mutations: { retry: false, networkMode: 'always' },
    },
  })
}
